import type { TFurniture } from "types/elevationReferences.type"

export const isCountertop = (item: TFurniture) => item.top_level_category?.name === "Countertops"

/**
 * Wall-mounted (upper) cabinets — the types whose labels sit ABOVE their highlight.
 *
 * `super_category` is the cabinet FAMILY. Of the 19 families `GET /categories` ships, exactly two
 * hang on the wall — `"wall cabinet"` (Casework) and `"locker cabinet - wall"` (Speciality) — so the
 * test is a pattern rather than two literals, to survive a rename or a new sibling family.
 *
 * BOTH "wall" and "cabinet" are required. "wall" alone is too loose to be safe: it would also catch
 * any future non-cabinet family that merely mentions one (wall panels, wall guards, wall
 * protection), silently lifting those labels above their boxes and into the wall-cabinet row. Today
 * the two rules select exactly the same families; the conjunction is what keeps that true later.
 *
 * Deliberately NOT tested against the leaf `category`: four leaves mention a wall without being wall
 * cabinets (`"filler panels - wall side filler"` and its siblings,
 * `"counter support brkts - in-wall brackets"`). The leaf is consulted only when the family is
 * missing, and then only for the `"wall cabinet - …"` prefix every Casework wall leaf carries.
 */
export const isWallCabinet = (item: TFurniture) => {
	const family = item.super_category?.name
	if (family) return /wall/i.test(family) && /cabinet/i.test(family)
	return /^wall cabinet/i.test(item.category?.name ?? "")
}
export const isModelConfirmed = (item: TFurniture) => item.selected_model_number !== null

/**
 * A custom object (#1346 / #1371) — an object priced from a drawing-local Custom Model Number rather
 * than from the Stevens catalogue.
 *
 * Tested on `custom_object_config` ALONE, which holds the backend's own invariant: it exists if and
 * only if the furniture is in custom-object mode — created there, or switched into it by the
 * custom-model-number PATCH — whether or not a definition has been picked yet.
 *
 * Deliberately NOT derived from `category`. The live API represents a custom object's `category` as
 * the bare string `"custom"` rather than the object-or-null shape every other category reads as (see
 * `.claude/context/api/drift.md`), and the related-category PATCH response omits `category`
 * altogether — so a detection keyed off it would read a freshly converted object as an "Other" for
 * as long as the cache held the spliced response.
 */
export const isCustomFurniture = (item: TFurniture) => item.custom_object_config != null

/**
 * An "Other" (fixed-price) object: no category on any level.
 *
 * The custom-object exclusion is load-bearing, not defensive. A custom object also has no category,
 * so without it every custom object would answer `true` here and be rendered — labelled, classified,
 * batched — as an "Other". The two share a shape on the wire and are different products.
 */
export const isOtherFurniture = (item: TFurniture) =>
	!isCustomFurniture(item) &&
	item.top_level_category == null &&
	item.super_category == null &&
	item.category == null

/**
 * Plain-text label for a furniture item, mirroring the branches rendered by
 * `FurnitureModelLabel`. Used where a string (not JSX) is needed — e.g. the
 * detach confirmation modal title and the "… has been detached" toast.
 */
export const getFurnitureDisplayLabel = (item: TFurniture): string => {
	if (isCustomFurniture(item)) return item.custom_object_config?.custom_model_number?.model_number ?? "Custom"
	if (isOtherFurniture(item)) return item.fixed_price_config?.name?.trim() || "Other"
	if (isCustomFurniture(item)) return item.custom_object_config?.custom_model_number?.model_number || "Custom"
	if (isModelConfirmed(item)) return item.selected_model_number!.model_number
	if (isCountertop(item)) return "Countertop"
	return "Model not confirmed"
}

/**
 * Names a furniture item in destructive-action copy: the model number once the user has picked
 * one, otherwise the category the detection resolved to.
 *
 * Deliberately NOT `getFurnitureDisplayLabel`. That one mirrors the on-canvas label, whose
 * "Model not confirmed" branch names the item's STATE rather than the item — fine on a highlight,
 * wrong in a confirmation dialog, where the user needs to recognise what they are about to lose.
 */
export const getFurnitureActionLabel = (item: TFurniture): string => {
	if (isCustomFurniture(item)) return item.custom_object_config?.custom_model_number?.model_number ?? "Custom"
	if (isOtherFurniture(item)) return item.fixed_price_config?.name?.trim() || "Other"
	if (isCustomFurniture(item)) return item.custom_object_config?.custom_model_number?.model_number || "Custom"
	// The leaf category can be missing while a coarser level is set, so walk up rather than stopping
	// at `category`. Returns "" when nothing names the item — callers fall back to generic copy.
	return (
		item.selected_model_number?.model_number ||
		item.category?.name ||
		item.super_category?.name ||
		item.top_level_category?.name ||
		""
	).trim()
}

/**
 * Returns the furniture as an "Other" item: every category level forced to null,
 * all other fields preserved. The category levels must be cleared explicitly
 * (not left to the PATCH response) because `spliceFurnitureIntoCache` merges, so
 * a stale category would otherwise survive and hide the "Other" label.
 * Invariant: `isOtherFurniture(asOtherFurniture(item))` is always true.
 *
 * `custom_object_config` is cleared for the SAME reason, and keeping the invariant true is why it
 * has to be: converting a custom object to an Other splices the response into the cache without
 * refetching, so a surviving config would make `isCustomFurniture` — and therefore every label,
 * classification and batch section — go on reporting the object as custom. The backend drops the
 * config on this transition too, so this only mirrors it locally.
 */
export const asOtherFurniture = (item: TFurniture): TFurniture => ({
	...item,
	category: null,
	super_category: null,
	top_level_category: null,
	custom_object_config: null,
})

/**
 * Returns the furniture as a CUSTOM object: every category level cleared, the Stevens model number
 * disconnected, and a `custom_object_config` in place — mirroring what the backend does for
 * `related_category_id: "custom"`.
 *
 * Needed for the same reason as `asOtherFurniture`: `spliceFurnitureIntoCache` MERGES, so a
 * response that simply omits the old category would leave the stale one in the cache and the object
 * would keep rendering as a cabinet. An existing config is preserved, so re-applying this to an
 * object that already has a linked definition never drops the link.
 */
export const asCustomFurniture = (item: TFurniture): TFurniture => ({
	...item,
	category: null,
	super_category: null,
	top_level_category: null,
	selected_model_number: null,
	custom_object_config: item.custom_object_config ?? { custom_model_number: null },
})
